import * as React from "react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
// import { RootState } from '@/lib/userType';
import { useGetSingleStudentQuery } from "@/store/studentApi";
import { useGetBatchQuery } from "@/store/batchApi";
import {
  useGetInterviewsByBatchQuery,
  useGetStudentInterviewsQuery,
} from "@/store/instructor/interview";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import InterviewHeader from "./interview/InterviewHeader";
import InterviewRankings from "./interview/InterviewRankings";
import InterviewList from "./interview/InterviewList";
import { useInterviewData } from "@/hooks/useInterviewData";
import { Batch, RootState } from "@/types";

const Interview = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: student, isLoading: isLoadingStudent } =
    useGetSingleStudentQuery(user?._id);
  const { data: allBatches, isLoading: isLoadingBatches } = useGetBatchQuery();

  // Find the student's batch
  const studentBatch = useMemo(() => {
    if (!student?.batch || !allBatches) return null;
    return allBatches.find((batch: Batch) => batch.bCode === student.batch);
  }, [student?.batch, allBatches]);

  // Fetch interviews for the student's batch
  const {
    data: batchInterviews,
    isLoading: isLoadingInterviews,
    error: interviewsError,
  } = useGetInterviewsByBatchQuery(
    {
      batchCode: studentBatch?._id || "",
      Token: user?.token || "",
    },
    {
      skip: !studentBatch?._id || !user?.token,
    }
  );

  const { data: studentAttemptsData, isLoading: isLoadingAttempts } =
    useGetStudentInterviewsQuery(
      {
        studentId: student?._id || "",
        batchCode: studentBatch?._id || "",
        Token: user?.token || "",
      },
      {
        skip: !student?._id || !studentBatch?._id || !user?.token,
      }
    );

  const studentAttempts = useMemo(() => {
    const raw =
      (studentAttemptsData)?.data ?? studentAttemptsData;
    return Array.isArray(raw) ? raw : [];
  }, [studentAttemptsData]);

  // Use custom hook to process interview data
  const { studentInterviews, ranks, hasAnyRank } = useInterviewData({
    interviews: batchInterviews?.data,
    studentId: student?._id,
    studentAttempts,
    sortAscending: false,
  });

  // Loading state
  if (
    isLoadingStudent ||
    isLoadingBatches ||
    isLoadingInterviews ||
    isLoadingAttempts
  ) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <Card>
          <CardContent className="p-8">
            <div className="text-center py-8">
              <p className="text-gray-500">Loading interview information...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No batch assigned
  if (!studentBatch) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <Card>
          <CardContent className="p-8">
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">
                No batch assigned. Please contact your instructor.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-6">
      <InterviewHeader
        title="Interview Dashboard"
        batchCode={studentBatch.bCode}
        courseName={studentBatch.courseName}
      />

      {hasAnyRank && (
        <InterviewRankings ranks={ranks} />
        // <InterviewRankings
        //     ranks={ranks}
        //     interviews={studentInterviews}
        //     title="Your Performance"
        // />
      )}

      <InterviewList
        interviews={studentInterviews}
        error={interviewsError}
        batchInfo={{
          bCode: studentBatch.bCode,
          _id: studentBatch._id,
        }}
        showInstructor={true}
      />
    </div>
  );
};

export default Interview;
